import { useEffect } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import type { QuizOption } from "@/react/types/QuizOption";
import { Button } from "@circle-react-uikit/Button";
import { Form } from "@circle-react-uikit/Form";
import { FormTextAreaAutosize } from "@circle-react-uikit/Form/FormTextAreaAutosize";
import { ImageInput } from "@circle-react-uikit/ImageInput";
import { Typography } from "@circle-react-uikit/Typography";
import { ImageUploadBlock } from "./ImageUploadBlock";
import { OptionItem } from "./OptionItem";
import type { QuestionPayload } from "./index";

export interface OptionPayload extends Omit<QuizOption, "id"> {
  _destroy?: boolean;
  id?: number;
  value: string;
}

interface QuestionFormProps {
  checkCorrectAnswersError: (
    payload: Omit<QuestionPayload, "cover_media">,
  ) => boolean;
  hasCorrectAnswersError: boolean;
  setIsCoverMediaEnabled: (value: boolean) => void;
}

export const QuestionForm = ({
  checkCorrectAnswersError,
  hasCorrectAnswersError,
  setIsCoverMediaEnabled,
}: QuestionFormProps) => {
  const { setValue, watch, getValues } = useFormContext();
  const translationRoot = "courses.edit.lesson";
  const { fields, append, update } = useFieldArray({
    name: "options",
  });

  const options: OptionPayload[] = watch("options");
  const visibleOptions = options.filter(option => !option._destroy);
  const questionType: "single_choice" | "multiple_choice" =
    watch("question_type");
  const isCoverMediaEnabled = watch("cover_media_enabled");

  const selectSingleCorrectAnswer = (optionIndex: number) => {
    fields.forEach((_option, index) => {
      setValue(`options[${index}].correct`, index === optionIndex);
    });
  };

  const onToggleCoverImageEnabled = (checked: boolean) => {
    setIsCoverMediaEnabled(checked);
    setValue("cover_media_enabled", checked);
  };

  const resetCorrectAnswers = () => {
    const options: OptionPayload[] = getValues("options");
    const firstCorrectIndex = options.findIndex(option => option.correct);
    fields.forEach((_option, index) => {
      if (firstCorrectIndex !== -1 && index === firstCorrectIndex) {
        setValue(`options[${index}].correct`, true);
      } else {
        setValue(`options[${index}].correct`, false);
      }
    });
  };

  useEffect(() => {
    checkCorrectAnswersError({ options, question_type: questionType });
  }, [options, questionType, checkCorrectAnswersError]);

  // enable the image toggle for the first time if there is a cover media coming from the API
  useEffect(() => {
    onToggleCoverImageEnabled(!!getValues("cover_media[signed_id]"));
  }, []);

  return (
    <>
      <Form.Item
        name="question_type"
        label={t([translationRoot, "question_type"])}
        className="!pb-0"
        labelClassName="!mb-2"
        hideDescription
        hideBorder
      >
        <Form.RadioGroup
          name="question_type"
          variant="box"
          className="!grid-cols-2"
          onChange={resetCorrectAnswers}
          options={[
            {
              label: t([translationRoot, "single_choice"]),
              value: "single_choice",
            },
            {
              label: t([translationRoot, "multiple_choice"]),
              value: "multiple_choice",
            },
          ]}
        />
      </Form.Item>
      <Form.Item
        name="cover_media_enabled"
        label={t([translationRoot, "image"])}
        className="!mt-10 !flex items-center justify-between !py-0"
        labelClassName="!mb-1"
        hideDescription
        hideBorder
      >
        <Form.ToggleSwitch
          name="cover_media_enabled"
          variant="small"
          onToggleChange={onToggleCoverImageEnabled}
        />
      </Form.Item>
      {isCoverMediaEnabled && (
        <Form.Item name="cover_media[signed_id]" hideDescription hideBorder>
          <ImageInput
            name="cover_media[signed_id]"
            aspectRatio={1468 / 536}
            customAspectRatioHelpText={t([translationRoot, "cover_media_help"])}
            renderer={ImageUploadBlock}
          />
        </Form.Item>
      )}
      <Form.Item
        required
        id="statement"
        name="statement"
        label={t([translationRoot, "question"])}
        placeholder={t([translationRoot, "question_placeholder"])}
        labelClassName="!mb-2"
        className="!mt-9 !py-0"
        hideDescription
        hideBorder
      >
        <FormTextAreaAutosize
          name="statement"
          rows={1}
          className="form-control text-default flex w-full rounded-lg ring-current focus-within:ring-1"
        />
      </Form.Item>
      <div className="mb-2 mt-10 flex flex-col">
        <Typography.BodyMd
          color="text-dark"
          weight="font-semibold"
          size="text-[15px]"
        >
          {t([translationRoot, "responses"])}
        </Typography.BodyMd>
        <Typography.LabelXs
          color={hasCorrectAnswersError ? "text-red-500" : "text-dark"}
        >
          {t([
            translationRoot,
            questionType === "single_choice"
              ? "select_correct_answer"
              : "select_all_correct_answers",
          ])}
        </Typography.LabelXs>
      </div>
      <div className="flex flex-col gap-4">
        {fields.map((option, optionIndex) => (
          <OptionItem
            optionIndex={optionIndex}
            onRemove={update}
            questionType={questionType}
            checkCorrectAnswersError={checkCorrectAnswersError}
            hasCorrectAnswersError={hasCorrectAnswersError}
            {...(questionType === "single_choice" && {
              onSelect: () => selectSingleCorrectAnswer(optionIndex),
            })}
            key={option.id}
          />
        ))}
      </div>
      {visibleOptions.length < 5 && (
        <Button
          type="button"
          variant="text"
          className="text-messaging-info-blue mb-10 mt-3"
          onClick={() =>
            append({
              value: `${t([translationRoot, "option"])} ${
                visibleOptions.length + 1
              }`,
              correct: false,
            })
          }
        >
          + {t([translationRoot, "add_option"])}
        </Button>
      )}
    </>
  );
};
